import React from "react";
import {
  TrendingUp,
  Building2,
  Users,
  Globe,
  Briefcase,
  BarChart3,
  Activity,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  Legend,
  Label,
  LabelList,
} from "recharts";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
// ========================
// Data (from SBA 2023 UT profile)
// ========================
const headline = {
  smallBusinesses: 371569,
  shareOfBusinesses: 99.4,
  smallBizEmployees: 690069,
  shareOfEmployees: 45.4,
  netEstablishmentChange: 2677,
  netJobsChange: 24200,
  netJobsFromSmallBiz: 21320,
  smallExporterShare: 85.1,
  smallExporterValueShare: 60.2,
  totalExportsBillion: 16.4,
  smallExportsBillion: 9.9,
  employmentGrowth1998_2022: 63.7,
};

const lending = {
  loansToRevUnder1M_Millions: 753.8,
  loansUnder100k_Billions: 1.1,
  loansAtOrUnder1M_Billions: 2.6,
};

const byIndustryCounts = [
  { name: "Prof., Sci. & Tech.", count: 62543 },
  { name: "Real Estate", count: 48058 },
  { name: "Retail Trade", count: 32682 },
  { name: "Construction", count: 35362 },
  { name: "Other Services", count: 33906 },
  { name: "Transport & Warehousing", count: 29394 },
];

const ownershipShares = [
  { name: "Women-owned", value: 44.5 },
  { name: "Veteran-owned", value: 4.4 },
  { name: "Hispanic-owned", value: 10.8 },
  { name: "Racial minority-owned", value: 5.2 },
];

const OWNERSHIP_COLORS = {
  "Women-owned": "#EC4899",
  "Hispanic-owned": "#F59E0B",
  "Veteran-owned": "#82E0AA",
  "Racial minority-owned": "#E67E22",
};

const firmSizeSplit = [
  { name: "Small Firms (1–499)", value: 690069 },
  { name: "Large Firms (500+)", value: 768905 },
];

const payrollSplit = [
  { name: "Small Firms", value: 40.1 },
  { name: "Large Firms", value: 59.9 },
];

const turnover = [
  { label: "Openings", value: 17190 },
  { label: "Closings", value: 14513 },
  { label: "Expansions", value: 25187 },
  { label: "Contractions", value: 22587 },
];

const selfEmployment = [
  { name: "Male Inc", value: 66169 },
  { name: "Female Inc", value: 31313 },
  { name: "Male Uninc", value: 101896 },
  { name: "Female Uninc", value: 62486 },
];

const SELF_EMPLOYMENT_COLORS = {
  "Male Inc": "#0047AB", // Darker Blue
  "Female Inc": "#FF1493", // Deep Pink
  "Male Uninc": "#6495ED", // Lighter Blue
  "Female Uninc": "#FFC0CB", // Light Pink
};

const industryEmploymentShare = [
  { industry: "Construction", small: 90.4, large: 9.6 },
  { industry: "Prof. Sci. & Tech.", small: 59.5, large: 40.5 },
  { industry: "Health Care & Social Assistance", small: 53.0, large: 47.0 },
  { industry: "Retail Trade", small: 39.3, large: 60.7 },
  { industry: "Real Estate & Rental Leasing", small: 87.4, large: 12.6 },
  { industry: "Other Services", small: 67.0, large: 33.0 },
  { industry: "Admin & Waste Mgmt", small: 57.6, large: 42.4 },
  { industry: "Accommodation & Food Services", small: 61.4, large: 38.6 },
  { industry: "Finance & Insurance", small: 27.9, large: 72.1 },
  { industry: "Manufacturing", small: 39.1, large: 60.9 },
  { industry: "Wholesale Trade", small: 48.4, large: 51.6 },
  { industry: "Transport & Warehousing", small: 39.4, large: 60.6 },
  { industry: "Arts, Entertainment & Rec.", small: 52.8, large: 47.2 },
  { industry: "Educational Services", small: 60.1, large: 39.9 },
  { industry: "Information", small: 29.1, large: 70.9 },
  { industry: "Mining, Quarrying, Oil & Gas", small: 41.1, large: 58.9 },
  { industry: "Management of Companies", small: 15.6, large: 84.4 },
  { industry: "Utilities", small: 19.0, large: 81.0 },
  {
    industry: "Agriculture, Forestry, Fishing & Hunting",
    small: 97.4,
    large: 2.6,
  },
];

const ownershipWorkforce = [
  { group: "Women-owned", firms: 44.5, workers: 43.9 },
  { group: "Veteran-owned", firms: 4.4, workers: 3.0 },
  { group: "Hispanic-owned", firms: 10.8, workers: 14.3 },
];

const longRunComparison = [
  { place: "Utah", growth: 63.7 },
  { place: "U.S.", growth: 33.7 },
];

const industryPayrollShare = [
  { industry: "Construction", small: 73.5, large: 26.5 },
  { industry: "Prof. Sci. & Tech.", small: 58.8, large: 41.2 },
  { industry: "Health Care & Social Assistance", small: 48.4, large: 51.6 },
  { industry: "Retail Trade", small: 29.4, large: 70.6 },
  { industry: "Real Estate & Rental Leasing", small: 83.7, large: 16.3 },
  { industry: "Other Services", small: 43.1, large: 56.9 },
  { industry: "Admin & Waste Mgmt", small: 40.6, large: 59.4 },
  { industry: "Accommodation & Food Services", small: 47.1, large: 52.9 },
  { industry: "Finance & Insurance", small: 27.9, large: 72.1 },
  { industry: "Manufacturing", small: 31.3, large: 68.7 },
  { industry: "Wholesale Trade", small: 48.4, large: 51.6 },
  { industry: "Transport & Warehousing", small: 30.4, large: 69.6 },
  { industry: "Arts, Entertainment & Rec.", small: 42.2, large: 57.8 },
  { industry: "Educational Services", small: 40.1, large: 59.9 },
  { industry: "Information", small: 29.2, large: 70.8 },
  { industry: "Mining, Quarrying, Oil & Gas", small: 36.5, large: 63.5 },
  { industry: "Management of Companies", small: 15.6, large: 84.4 },
  { industry: "Utilities", small: 18.0, large: 82.0 },
  {
    industry: "Agriculture, Forestry, Fishing & Hunting",
    small: 90.7,
    large: 9.3,
  },
];

// Sorted copies for clearer display
const sortedEmployment = [...industryEmploymentShare].sort(
  (a, b) => b.small - a.small
);
const sortedPayroll = [...industryPayrollShare].sort(
  (a, b) => b.small - a.small
);

const COLORS = [
  "#014CB2",
  "#fa9a25",
  "#0ea5e9",
  "#10b981",
  "#6366f1",
  "#f97316",
];

const fmtInt = (n) => n.toLocaleString("en-US");
const fmtPct = (n) => `${n}%`;
const fmtMoneyB = (n) => `$${n.toFixed(1)}B`;
const fmtMoneyM = (n) => `$${n.toFixed(1)}M`;

// Custom outside label to avoid overlap
const OutsideLabel = ({
  x,
  y,
  width,
  height,
  value,
  color = "#0ea5e9",
  align = "right",
}) => {
  if (value === undefined || value === null) return null;
  const tx = align === "left" ? x - 8 : x + width + 8;
  const anchor = align === "left" ? "end" : "start";
  return (
    <text
      x={tx}
      y={y + height / 2 + 4}
      textAnchor={anchor}
      fontSize={12}
      fontWeight="700"
      fill={color}
    >
      {`${value}%`}
    </text>
  );
};

const Card = ({ children, className }) => (
  <div
    className={`rounded-xl border bg-white shadow-sm p-3 ${className || ""}`}
  >
    {children}
  </div>
);
const CardHeader = ({ children }) => <div className="mb-1">{children}</div>;
const CardTitle = ({ children }) => (
  <h3 className="text-sm font-semibold">{children}</h3>
);
const CardContent = ({ children }) => <div>{children}</div>;

// Custom tick to wrap long industry labels on X axis
const IndustryTick = ({ x, y, payload }) => {
  const value = payload?.value || "";
  // Wrap only when needed
  const lines =
    value === "Transport & Warehousing"
      ? ["Transport &", "Warehousing"]
      : [value];
  return (
    <text
      x={x}
      y={y + 10}
      textAnchor="end"
      fill="#111"
      fontSize={12}
      transform={`rotate(-20, ${x}, ${y})`}
    >
      {lines.map((line, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 0 : 12}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

const Stat = ({ icon: Icon, label, value, sub }) => (
  <Card>
    <CardHeader>
      <div className="flex items-center gap-2 text-slate-700">
        <Icon className="h-4 w-4" /> <CardTitle>{label}</CardTitle>
      </div>
    </CardHeader>
    <CardContent>
      <div className="text-xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

export default function UtahSmallBusinessAtAGlance() {
  return (
    // <div className="min-h-screen w-full bg-gradient-to-b from-slate-50 to-slate-100">
    <div>
      <Header />
      <main className="min-h-screen relative overflow-hidden mt-[6rem] bg-transparent text-slate-900">
        {/* Top centered title */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-2 text-center select-none">
          <p className="text-[21px] sm:text-md font-semibold uppercase tracking-widest text-[#1e3a8a]">
            U.S. SMALL BUSINESS ADMINISTRATION
          </p>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900">
            <span>OFFICE OF ADVOCACY</span>
            {/* <span className="bg-gradient-to-r from-[#0b2c5a] to-[#3b5fa3] bg-clip-text text-transparent">ADVOCACY</span> */}
          </h2>
        </div>
        {/* Header */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 flex justify-between">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-xl md:text-2xl  font-bold tracking-tight text-slate-900">
              Utah Small Business — 2025 At-a-Glance
            </h1>
            <p className="mt-1 text-slate-600 max-w-2xl text-sm">
              A full snapshot of Utah’s small-business landscape: scale, jobs,
              lending, exports, ownership, turnover, demographics, and industry
              employment shares.
            </p>
          </motion.div>
          <p className="text-xs text-slate-500">
            <b>Source:</b>&nbsp;
            <a
              href="/Utah_2025-State-Profile.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Small Business Profile 2025 (Utah)
            </a>
          </p>
        </div>

        {/* Global Color Legend */}
        {/* <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mb-4">
          <div className="flex flex-wrap gap-4 text-xs text-slate-700">
            <div className="flex items-center gap-1">
              <span className="h-3 w-3 bg-[#0ea5e9] rounded-sm" /> Small Firms / Firms %
            </div>
            <div className="flex items-center gap-1">
              <span className="h-3 w-3 bg-[#6366f1] rounded-sm" /> Large Firms / Workers %
            </div>
          </div>
        </div> */}

        {/* KPI Grid */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat
            icon={Building2}
            label="Small businesses"
            value={fmtInt(headline.smallBusinesses)}
            sub={`${fmtPct(headline.shareOfBusinesses)} of all firms`}
          />
          <Stat
            icon={Users}
            label="Employed by small biz"
            value={fmtInt(headline.smallBizEmployees)}
            sub={`${fmtPct(headline.shareOfEmployees)} of workforce`}
          />
          <Stat
            icon={TrendingUp}
            label="Net jobs added"
            value={fmtInt(headline.netJobsChange)}
            sub={`Small biz: ${fmtInt(headline.netJobsFromSmallBiz)} (${fmtPct(88.1)})`}
          />
          <Stat
            icon={Briefcase}
            label="Net establishments"
            value={`+${fmtInt(headline.netEstablishmentChange)}`}
            sub="Openings − closings (’21→’22)"
          />
        </div>

        {/* Turnover Stats */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {turnover.map((t, i) => (
            <Stat
              key={i}
              icon={Activity}
              label={t.label}
              value={fmtInt(t.value)}
            />
          ))}
        </div>

        {/* Charts Row */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Top industries */}
          <Card>
            <CardHeader>
              <CardTitle>Top industries by small-business count</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={byIndustryCounts}
                    layout="vertical"
                    margin={{ top: 20, right: 20, left: 5, bottom: 35 }}
                    barCategoryGap="15%"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    {/* domain={[0, 'dataMax + 2000']} offset={20} */}
                    <XAxis
                      type="number"
                      tickFormatter={(v) => fmtInt(v)}
                      tick={{ fontSize: 12, fill: "#111" }}
                      tickMargin={6}
                    >
                      <Label
                        value="Firms (count)"
                        position="bottom"
                        style={{ fill: "#111" }}
                      />
                    </XAxis>
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={90}
                      tick={{ fontSize: 12, fill: "#111" }}
                    >
                      <Label
                        value="Industries"
                        angle={-90}
                        position="insideLeft"
                        style={{ fill: "#111", textAnchor: "middle" }}
                        dy={-60}
                      />
                    </YAxis>
                    <Tooltip formatter={(v) => fmtInt(v)} />
                    {/* <Legend verticalAlign="top" align="right" /> */}
                    <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={20}>
                      {byIndustryCounts.map((d, i) => {
                        const overrides = {
                          Construction: "#96F7E4",
                          "Retail Trade": "#F4A8FF",
                        };
                        const fill =
                          overrides[d.name] || COLORS[i % COLORS.length];
                        return <Cell key={i} fill={fill} />;
                      })}
                      {/* <LabelList dataKey="count" position="right" offset={6} formatter={(v) => fmtInt(v)} style={{ fill: '#111' }} /> */}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Ownership shares */}
          <Card>
            <CardHeader>
              <CardTitle>Ownership snapshot</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={ownershipShares}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                    >
                      {ownershipShares.map((s, i) => (
                        <Cell
                          key={i}
                          fill={
                            OWNERSHIP_COLORS[s.name] ||
                            COLORS[i % COLORS.length]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => fmtPct(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-1 grid grid-cols-1 gap-1 text-xs">
                {ownershipShares.map((s, i) => (
                  <li key={s.name} className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{
                        background:
                          OWNERSHIP_COLORS[s.name] || COLORS[i % COLORS.length],
                      }}
                    />
                    <span className="text-slate-600">{s.name}</span>
                    <span className="ml-auto font-medium text-slate-900">
                      {fmtPct(s.value)}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Financing & Exports */}
          <Card>
            <CardHeader>
              <CardTitle>Financing & global reach</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                <Stat
                  icon={BarChart3}
                  label="Loans ≤$1M revenue"
                  value={fmtMoneyM(lending.loansToRevUnder1M_Millions)}
                  sub="CRA (2023)"
                />
                <Stat
                  icon={BarChart3}
                  label="Loans ≤$100k"
                  value={fmtMoneyB(lending.loansUnder100k_Billions)}
                  sub="(2023)"
                />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Stat
                  icon={Globe}
                  label="Exporters small"
                  value={fmtPct(headline.smallExporterShare)}
                  sub={`Small-firm exports ${fmtMoneyB(headline.smallExportsBillion)} / ${fmtMoneyB(headline.totalExportsBillion)}`}
                />
                <Stat
                  icon={Globe}
                  label="Export value small firms"
                  value={fmtPct(headline.smallExporterValueShare)}
                  sub="(2023)"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Firm size split */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card>
            <CardHeader>
              <CardTitle>Employment by firm size</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={firmSizeSplit}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={70}
                  >
                    {firmSizeSplit.map((slice, i) => (
                      <Cell
                        key={i}
                        fill={
                          slice.name.includes("Small") ? "#2984D1" : "#A4F4CF"
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtInt(v)} />
                  <Legend verticalAlign="bottom" align="center" />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Payroll share by firm size</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={payrollSplit}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={70}
                  >
                    {payrollSplit.map((slice, i) => (
                      <Cell
                        key={i}
                        fill={
                          slice.name.includes("Small") ? "#2984D1" : "#A4F4CF"
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtPct(v)} />
                  <Legend verticalAlign="bottom" align="center" />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Self Employment */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-5">
          <Card>
            <CardHeader>
              <CardTitle>Self-employment snapshot (2020)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart
                  data={selfEmployment}
                  margin={{ top: 10, right: 10, left: 20, bottom: 50 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#111" }}
                    interval={0}
                    angle={-15}
                    textAnchor="end"
                    height={20}
                    tickMargin={8}
                  >
                    <Label
                      value="Category"
                      position="bottom"
                      offset={25}
                      style={{ fill: "#111" }}
                    />
                  </XAxis>
                  <YAxis tick={{ fontSize: 12, fill: "#111" }} tickMargin={6}>
                    <Label
                      value="People"
                      angle={-90}
                      position="insideLeft"
                      offset={0}
                      style={{ fill: "#111" }}
                    />
                  </YAxis>
                  <Tooltip formatter={(v) => fmtInt(v)} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {selfEmployment.map((s, i) => (
                      <Cell
                        key={i}
                        fill={
                          SELF_EMPLOYMENT_COLORS[s.name] ||
                          COLORS[i % COLORS.length]
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
        {/* Ownership vs Workforce */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-5">
          <Card>
            <CardHeader>
              <CardTitle>Ownership vs Workforce share</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart
                  data={ownershipWorkforce}
                  margin={{ top: 32, right: 10, left: 20, bottom: 44 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="group"
                    tick={{ fontSize: 12, fill: "#111" }}
                    interval={0}
                    angle={-15}
                    textAnchor="end"
                    height={20}
                    tickMargin={8}
                  >
                    <Label
                      value="Group"
                      position="bottom"
                      offset={28}
                      style={{ fill: "#111" }}
                    />
                  </XAxis>
                  <YAxis tick={{ fontSize: 12, fill: "#111" }} tickMargin={6}>
                    <Label
                      value="Share (%)"
                      angle={-90}
                      position="insideLeft"
                      offset={12}
                      style={{ fill: "#111" }}
                    />
                  </YAxis>
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Legend verticalAlign="top" align="right" />
                  <Bar dataKey="firms" fill="#034F3B" name="Firms %" />
                  <Bar dataKey="workers" fill="#737373" name="Workers %" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Long-Run Employment Growth */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-5">
          <Card>
            <CardHeader>
              <CardTitle>Employment Growth 1998–2022</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart
                  data={longRunComparison}
                  margin={{ top: 30, right: 10, left: 20, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="place"
                    tick={{ fontSize: 12, fill: "#111" }}
                    tickMargin={8}
                  >
                    <Label
                      value="Place"
                      position="bottom"
                      offset={20}
                      style={{ fill: "#111" }}
                    />
                  </XAxis>
                  <YAxis tick={{ fontSize: 12, fill: "#111" }} tickMargin={6}>
                    <Label
                      value="Growth (%)"
                      angle={-90}
                      position="insideLeft"
                      offset={0}
                      style={{ fill: "#111" }}
                    />
                  </YAxis>
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Bar dataKey="growth" name="Growth %">
                    {longRunComparison.map((d, i) => (
                      <Cell
                        key={`cell-${i}`}
                        fill={d.place === "Utah" ? "#D62828" : "#6E11B0"}
                      />
                    ))}
                    <LabelList
                      dataKey="growth"
                      position="top"
                      formatter={(v) => `${v}%`}
                      offset={2}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Industry Employment Shares */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-5">
          <Card>
            <CardHeader>
              <CardTitle>
                Industry employment share (small vs large firms)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={740}>
                <BarChart
                  data={industryEmploymentShare}
                  layout="vertical"
                  margin={{ top: 10, right: 20, left: 20, bottom: 50 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tickFormatter={(v) => `${v}%`}
                    tick={{ fontSize: 12, fill: "#111" }}
                    tickMargin={8}
                  >
                    <Label
                      value="Share (%)"
                      position="bottom"
                      offset={28}
                      style={{ fill: "#111" }}
                    />
                  </XAxis>
                  <YAxis
                    type="category"
                    dataKey="industry"
                    width={120}
                    tick={{ fontSize: 12, fill: "#111" }}
                    tickMargin={6}
                  >
                    <Label
                      value="Industry"
                      angle={-90}
                      position="left"
                      offset={0}
                      style={{ fill: "#111" }}
                    />
                  </YAxis>
                  <Tooltip formatter={(v) => fmtPct(v)} />
                  <Legend verticalAlign="top" align="right" />
                  <Bar
                    dataKey="small"
                    stackId="a"
                    fill="#2984D1"
                    name="Small Firms"
                  />
                  <Bar
                    dataKey="large"
                    stackId="a"
                    fill="#A4F4CF"
                    name="Large Firms"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Industry Payroll Shares */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-5">
          <Card>
            <CardHeader>
              <CardTitle>
                Industry payroll share (small vs large firms)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={720}>
                <BarChart
                  data={industryPayrollShare}
                  layout="vertical"
                  margin={{ top: 10, right: 20, left: 20, bottom: 50 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tickFormatter={(v) => `${v}%`}
                    tick={{ fontSize: 12, fill: "#111" }}
                  >
                    <Label
                      value="Share (%)"
                      position="bottom"
                      offset={0}
                      style={{ fill: "#111" }}
                    />
                  </XAxis>
                  <YAxis
                    type="category"
                    dataKey="industry"
                    width={150}
                    tick={{ fontSize: 12, fill: "#111" }}
                  >
                    <Label
                      value="Industry"
                      angle={-90}
                      position="left"
                      offset={0}
                      style={{ fill: "#111" }}
                    />
                  </YAxis>
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Legend verticalAlign="top" align="right" />
                  <Bar
                    dataKey="small"
                    stackId="a"
                    fill="#2984D1"
                    name="Small Firms"
                  />
                  <Bar
                    dataKey="large"
                    stackId="a"
                    fill="#A4F4CF"
                    name="Large Firms"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Highlight banner */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-5">
          <Card className="bg-blue-50">
            <CardContent className="py-4">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" /> Long-run momentum
                  </h3>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Small-business employment grew{" "}
                    {fmtPct(headline.employmentGrowth1998_2022)} in Utah from
                    1998–2022, outpacing the national rate.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer note */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-5">
          <p className="text-xs text-slate-500">
            Source: U.S. Small Business Administration, Office of Advocacy —{" "}
            <a
              href="/Utah_2025-State-Profile.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Small Business Profile 2025 (Utah)
            </a>
            . Figures rounded; see profile technical notes for definitions.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
